'use client'

import React, { Fragment } from 'react'
import Image from 'next/image'
import { cn, useMediaQuery } from '@segway/core'

import { Link } from '@/i18n/navigation'

import { ModuleTitle } from '../moduleTtile'
import Sliders from '../slider/Sliders'
import { BlockData, WhyChooseUsBlockData, WhyChooseUsItemData } from '../types'

import styles from './whyChooseUsV2.module.css'

type WhyChooseUsV2Props = {
  blockData: WhyChooseUsBlockData
}

const WhyChooseUsV2 = ({ blockData }: WhyChooseUsV2Props) => {
  const responsive = useMediaQuery()

  // For iPad and PC
  const isIpadScreen = responsive && responsive.md

  const renderWhyChooseUsItem = (item: WhyChooseUsItemData) => {
    if (item.top_block && item.bottom_block) {
      return (
        <div className={`flex flex-col ${isIpadScreen ? 'gap-4' : 'gap-[8px]'} h-full`}>
          {renderWhyChooseUsItemBlock(item.top_block)}
          {renderWhyChooseUsItemBlock(item.bottom_block)}
        </div>
      )
    }

    if (item.pc) {
      const data = item.pc
      const columns = item.pc?.columns === '2' ? 'xl:col-span-2' : 'xl:col-span-1'

      if (!data) return null

      const content = (
        <div className="">
          {data?.image && (
            <div>
              <Image
                src={data.image}
                alt={data.title || ''}
                fill
                className="rounded-md"
                sizes="100vw"
              />
            </div>
          )}
          <div className="relative top-0 flex-col gap-[12px] px-[32px] pt-[24px] text-[#000000] xl:flex">
            <div className="flex max-w-[300px] flex-col gap-[8px]">
              {data?.subtitle && (
                <p className="font-miSansMedium380 text-base leading-[1.5] text-primary">
                  {data?.subtitle}
                </p>
              )}
              {data?.title && <p className="font-miSansDemiBold450 text-2xl">{data?.title}</p>}
              {data?.description && (
                <p
                  className={`font-miSansRegular330 text-lg leading-[1.4] ${item.pc?.columns === '2' ? 'max-w-[220px]' : ''}`}>
                  {data?.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )

      const linkClassName = `relative flex flex-col justify-start overflow-hidden font-miSansDemiBold450 h-[480px] ${item.pc?.columns === '2' ? 'w-[712px]' : 'w-[348px]'} rounded-[20px] ${columns}`

      return item.link ? (
        <Link href={item.link} className={linkClassName}>
          {content}
        </Link>
      ) : (
        <div className={linkClassName}>{content}</div>
      )
    }
    return null
  }

  const renderWhyChooseUsMobileItem = (item: WhyChooseUsItemData) => {
    if (item.top_block && item.bottom_block) {
      return (
        <div className="flex h-full flex-col gap-2">
          {renderWhyChooseUsItemBlock(item.top_block)}
          {renderWhyChooseUsItemBlock(item.bottom_block)}
        </div>
      )
    }

    if (item.mob) {
      const data = item.mob
      const columns = 'xl:col-span-1'

      if (!data) return null

      const content = (
        <div className="">
          {data?.image && (
            <div className="relative w-full">
              <Image
                src={data.image}
                alt={data.title || ''}
                height={228}
                width={165}
                className="absolute h-[228px] w-[165px] rounded-md"
              />
            </div>
          )}
          <div className="relative top-0 flex-col gap-[12px] px-[15px] pt-[12px] text-[#000000] xl:flex">
            <div className="flex flex-col gap-[6px]">
              {data?.subtitle && (
                <p className="font-miSansMedium380 text-sm text-primary">{data?.subtitle}</p>
              )}
              {data?.title && (
                <p className="max-w-[100px] font-miSansDemiBold450 text-base">{data?.title}</p>
              )}
            </div>
          </div>
        </div>
      )

      const linkClassName = `relative flex h-[228px] w-full min-w-[165px] flex-col justify-start overflow-hidden rounded-[9.5px] font-miSansDemiBold450 ${columns}`

      return item.link ? (
        <Link href={item.link} className={linkClassName}>
          {content}
        </Link>
      ) : (
        <div className={linkClassName}>{content}</div>
      )
    }
    return null
  }

  const renderWhyChooseUsItemBlock = (block: BlockData) => {
    if (!block) return null

    const isMobile = !isIpadScreen

    const title = isMobile ? block.mob_title : block.pc_title
    const subtitle = isMobile ? block.mob_subtitle : block.pc_subtitle
    const image = isMobile ? block.mob_image : block.pc_image
    const description = !isMobile ? block.pc_description : ''

    const content = (
      <div
        className={`bg-[#EFF0F3] ${isMobile ? 'min-w-[165px] rounded-[9.5px] pb-[13px] pl-[16px] pr-[14px] pt-[13.77px]' : 'rounded-[20px] px-[30px] pb-[28px] pt-[26px]'} flex h-full flex-col justify-end`}>
        {image && (
          <div className={`relative min-h-0 xl:mb-[23px] ${isMobile ? '' : 'flex-1'} `}>
            <div className="">
              <Image
                src={image}
                alt="Affirm logo"
                width={120}
                height={40}
                className={`${isMobile ? 'mb-[5.7px] max-w-[64px]' : 'h-full max-w-[212px]'} w-full`}
              />
            </div>
          </div>
        )}
        <div
          className={`flex flex-col text-[#000000] ${isMobile ? '' : 'gap-[9px]'} flex-shrink-0`}>
          {subtitle && (
            <p
              className={`font-miSansMedium380 text-[12px] text-red-600 xl:text-base xl:leading-[1.5] ${isMobile ? 'mb-[6px]' : ''}`}>
              {subtitle}
            </p>
          )}
          {title && (
            <h3 className={`max-w-[200px] font-miSansDemiBold450 text-[14px] xl:text-2xl`}>
              {title}
            </h3>
          )}
          {description && (
            <p className="max-w-[230px] font-miSansRegular330 text-lg">{description}</p>
          )}
        </div>
      </div>
    )

    const linkClassName = `block ${isMobile ? 'h-[110px]' : 'h-[232px] w-[348px]'}`

    return block.link ? (
      <Link href={block.link} className={linkClassName}>
        {content}
      </Link>
    ) : (
      <div className={linkClassName}>{content}</div>
    )
  }

  return (
    <div className="flex w-full items-center justify-center">
      <div className="flex w-full flex-col justify-center">
        <div className="px-7 max-xl:px-4">
          <div className="mx-auto w-full 2xl:max-w-[1440px]">
            <ModuleTitle title={blockData?.name} viewAll="" />
          </div>
        </div>
        <div className="flex w-full">
          {isIpadScreen ? (
            <Sliders
              gap={16}
              items={blockData?.items?.map((item, index) => (
                <div
                  key={index}
                  className={cn(
                    'relative col-span-2 mr-[16px] h-full w-fit basis-auto bg-[#fff] max-xl:border-none max-xl:bg-[unset]',
                    {
                      'm-0': blockData.items.length === index + 1,
                    },
                  )}>
                  {renderWhyChooseUsItem(item)}
                </div>
              ))}
            />
          ) : (
            <div
              className={`horizontal-scrollable-container w-full overflow-x-auto max-xl:px-4 ${styles.whychooseus}`}>
              <div className="flex h-full w-fit gap-[12px]">
                {blockData?.items?.map((item, index) => (
                  <Fragment key={index}>{renderWhyChooseUsMobileItem(item)}</Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default WhyChooseUsV2
